import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';

export default class extends Controller {
  static targets = ['messages', 'newMessage', 'membersCount', 'room', 'roomName', 'roomLogo']

  static ACTION_TYPES = {
    CREATE_MESSAGE: 'create_user_message',
    UPDATE_MESSAGE: 'update_user_message',
    DELETE_MESSAGE: 'delete_user_message',
    ADD_ROOM_MESSAGE_REACTION: 'create_room_message_reaction',
    DELETE_ROOM_MESSAGE_REACTION: 'delete_room_message_reaction',
    UPDATE_ROOM_MESSAGE_PIN: 'update_room_message_pin',
    UPDATE_ROOM_NAME: 'update_room_name',
    UPDATE_ROOM_LOGO: 'update_room_logo',
    ADD_MEMBER: 'add_member',
    REMOVE_MEMBER: 'remove_member',
    LEAVE_ROOM: 'leave_room',
    UPDATE_MEMBERS_COUNT: 'update_members_count',
    ADD_ROOM_ADMIN: 'add_room_admin',
    REMOVE_ROOM_ADMIN: 'remove_room_admin'
  };

  static CLASSES = {
    MESSAGE: 'room-message',
    MESSAGE_CONTENT: 'room-message__content',
    MESSAGE_BUBBLE: 'room-message__bubble',
    MESSAGE_CONTAINER: 'room-message__container',
    MESSAGE_STATUS: 'room-message__status',
    MESSAGE_IMAGES: 'room-message__images',
    MESSAGE_ACTIONS: 'room-message__actions',
    MESSAGE_MENU: 'room-message__menu',
    MESSAGE_EDITED_STATUS: 'room-message__edited-status',
    MEMBER_TITLE_FIRST_PART: 'room-member__title-first-part',
    MEMBER_ROLE: 'room-member__role',
    MEMBER_MENU: 'room-member__menu',
    MEMBER_MENU_ADD_ADMIN: 'room-member__menu__add-admin',
    MEMBER_MENU_REMOVE_ADMIN: 'room-member__menu__remove-admin',
    MESSAGE_REACTIONS: 'room-message__reactions',
    MESSAGE_REACTIONS_EMOJIS: 'room-message__reactions__emojis',
    MESSAGE_REACTIONS_COUNT: 'room-message__reactions__count',
    MESSAGE_SOURCE_MESSAGE: 'room-message__source-message',
    MESSAGE_SOURCE_MESSAGE_CONTENT: 'room-message__source-message__content',
    MESSAGE_SOURCE_MESSAGE_IMAGE: 'room-room-message__source-message__image',
  };

  static IDS = {
    SIDE_PANEL_CONTENT: 'room-side-panel-content'
  };

  static DATA_ATTRS = {
    LEAVE_BUTTON: '[data-cy="leave"]',
    ACTION_TYPE_LEAVE: 'input[name="action_type"][value="leave"]'
  };

  profileId = null;
  roomId = null;
  subscription = null;

  connect() {
    this.roomId = this.data.get('id');
    this.profileId = this.data.get('profile-id');

    this.i18n = {
      react: this.data.get('react-label'),
      reply: this.data.get('reply-label'),
      info: this.data.get('info-label'),
      pin: this.data.get('pin-label'),
      unpin: this.data.get('unpin-label'),
      edit: this.data.get('edit-label'),
      delete: this.data.get('delete-label'),
      confirmDelete: this.data.get('confirm-delete-label'),
      everyoneLabel: this.data.get('everyone-label'),
      editedLabel: this.data.get('edited-label'),
    };

    this._createSubscription();
    this._updateUrl(this.roomId);
  }

  disconnect() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
    }
  }

  cancelReply() {
    const sourceMessage = document.getElementById(`source-message`);

    if (sourceMessage) {
      sourceMessage.classList.add('hidden');
      sourceMessage.innerHTML = '';
    }
  }

  focusInput() {
    setTimeout(() => {
      this.element.dispatchEvent(new CustomEvent('richEditor:focusInput', { bubbles: true }));
    }, 50);
  }

  _createSubscription() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
      this.subscription = null;
    }

    this.subscription = consumer.subscriptions.create(
      { channel: 'RoomChannel', id: this.roomId },
      {
        connected: this._connected.bind(this),
        disconnected: this._disconnected.bind(this),
        received: this._received.bind(this)
      }
    );
  }

  _connected() {
    this.scrollToBottom();
    this.subscription.perform('mark_as_read');
    this._toggleLeaveRoomAction();
  }

  _disconnected() {
  }

  _received(data) {
    if (!data?.payload) return;

    const actionHandlers = {
      [this.constructor.ACTION_TYPES.CREATE_MESSAGE]: this._createUserMessage,
      [this.constructor.ACTION_TYPES.UPDATE_MESSAGE]: this._updateUserMessage,
      [this.constructor.ACTION_TYPES.DELETE_MESSAGE]: this._deleteUserMessage,
      [this.constructor.ACTION_TYPES.ADD_ROOM_MESSAGE_REACTION]: this._addRoomMessageReaction,
      [this.constructor.ACTION_TYPES.DELETE_ROOM_MESSAGE_REACTION]: this._deleteRoomMessageReaction,
      [this.constructor.ACTION_TYPES.UPDATE_ROOM_MESSAGE_PIN]: this._updateRoomMessagePin,
      [this.constructor.ACTION_TYPES.UPDATE_ROOM_NAME]: this._updateRoomName,
      [this.constructor.ACTION_TYPES.UPDATE_ROOM_LOGO]: this._updateRoomLogo,
      [this.constructor.ACTION_TYPES.ADD_MEMBER]: this._addMember,
      [this.constructor.ACTION_TYPES.REMOVE_MEMBER]: this._removeMember,
      [this.constructor.ACTION_TYPES.LEAVE_ROOM]: this._leaveRoom,
      [this.constructor.ACTION_TYPES.UPDATE_MEMBERS_COUNT]: this._updateMembersCount,
      [this.constructor.ACTION_TYPES.ADD_ROOM_ADMIN]: this._addRoomAdmin,
      [this.constructor.ACTION_TYPES.REMOVE_ROOM_ADMIN]: this._removeRoomAdmin
    };

    const handler = actionHandlers[data.payload.type];
    if (handler) {
      handler.call(this, data.payload);
    }
  }

  _createUserMessage(payload) {
    const isOwnMessage = this._isOwnMessage(payload.profileId);
    const messageHTML = this._buildMessageHTML(payload, isOwnMessage);

    this.messagesTarget.insertAdjacentHTML('beforeend', messageHTML);
    this.scrollToBottom();

    this.subscription.perform('mark_as_read');
  }

  _updateUserMessage(payload) {
    const messageContainer = this._getMessageContainer(payload.id);
    if (!messageContainer) return;

    const contentElement = messageContainer.querySelector(this._sel('MESSAGE_CONTENT'));
    if (!contentElement) return;

    contentElement.innerHTML = (payload.content !== null && payload.content !== undefined) ? payload.content.replaceAll('__everyone_label', this.i18n.everyoneLabel) : null;
    this._addEditedLabel(messageContainer, `(${this.i18n.editedLabel})`);

    this._updateSourceMessageReferences(payload.id, payload.sourceMessageContent?.replaceAll('__everyone_label', this.i18n.everyoneLabel));
  }

  _updateSourceMessageReferences(messageId, newContent) {
    const sourceContainers = document.querySelectorAll(`.room-message__source-message-${messageId}`);

    sourceContainers.forEach(container => {
      const contentEl = container.querySelector('[class*="source-message__content"]');

      if (newContent && contentEl) {
        contentEl.textContent = newContent;
      } else if (newContent && !contentEl) {
        const authorEl = container.querySelector('.text-xs.font-bold');
        if (authorEl) {
          const newContentEl = document.createElement('div');
          newContentEl.className = `${this._cls('MESSAGE_SOURCE_MESSAGE_CONTENT')} text-sm`;
          newContentEl.textContent = newContent;
          authorEl.insertAdjacentElement('afterend', newContentEl);
        }
      } else if (!newContent && contentEl) {
        contentEl.remove();
      }
    });
  }

  _deleteUserMessage(payload) {
    const messageContainer = this._getMessageContainer(payload.id);
    if (!messageContainer) return;

    this._updateDeletedMessageContent(messageContainer, payload.content);
    this._removeMessageActions(messageContainer);
    this._removeSourceMessage(messageContainer);
    this._removeSourceMessageReferences(payload.id);
  }

  _addRoomMessageReaction(payload) {
    this._upsertReactions(payload);
  }

  _deleteRoomMessageReaction(payload) {
    if (payload.count === 0) {
      this._removeReactions(payload.messageId);
    } else {
      this._upsertReactions(payload);
    }
  }

  _updateRoomMessagePin(payload) {
    const pinFrame = document.getElementById(`room-message-pin-${payload.messageId}`);
    if (!pinFrame) return;

    pinFrame.innerHTML = this._buildPinMenuItem(payload.messageId, !!payload.pinned);
  }

  _upsertReactions(payload) {
    const messageContainer = this._getMessageContainer(payload.messageId);
    if (!messageContainer) return;

    let reactionsForm = messageContainer.querySelector(this._sel('MESSAGE_REACTIONS'));

    if (reactionsForm) {
      reactionsForm.querySelector(this._sel('MESSAGE_REACTIONS_EMOJIS')).textContent = payload.emojis.join(' ');
      reactionsForm.querySelector(this._sel('MESSAGE_REACTIONS_COUNT')).textContent = payload.count;
    } else {
      const container = messageContainer.querySelector(this._sel('MESSAGE_CONTAINER'));
      if (!container) return;
      container.insertAdjacentHTML('beforeend', this._buildReactionsHTML(payload));
    }
  }

  _removeReactions(messageId) {
    const messageContainer = this._getMessageContainer(messageId);
    if (!messageContainer) return;

    const reactionsForm = messageContainer.querySelector(this._sel('MESSAGE_REACTIONS'));
    if (reactionsForm) reactionsForm.remove();
  }

  _buildReactionsHTML(payload) {
    const isOwnMessage = payload.messageAuthorId === this.profileId;
    const alignClass = isOwnMessage ? 'ml-auto' : 'ml-4';
    const borderPaddingClass = payload.selfReaction ? 'border-2 border-secondary-regular p-0.5' : 'p-1';

    return `
    <form
      class="${this._cls('MESSAGE_REACTIONS')} mt-1 rounded bg-secondary-light hover:bg-secondary-regular text-xs select-none transition-colors duration-200 ${alignClass} ${borderPaddingClass}"
      method="get"
      action="/room_messages/${payload.messageId}/reactions"
    >
      <button data-turbo-frame="modal" type="submit">
        <span class="${this._cls('MESSAGE_REACTIONS_EMOJIS')}">${payload.emojis.join(' ')}</span>
        <span class="${this._cls('MESSAGE_REACTIONS_COUNT')} font-bold text-secondary-dark ml-1">${payload.count}</span>
      </button>
    </form>
  `;
  }

  _updateRoomName(payload) {
    this.roomNameTarget.innerHTML = payload.roomName;
    this._addSystemMessage(payload);
  }

  _updateRoomLogo(payload) {
    this.roomLogoTarget.innerHTML = this._buildRoomLogoHtml(payload);
    this._addSystemMessage(payload);
  }

  _addMember(payload) {
    this._addSystemMessage(payload);
    this._toggleLeaveRoomAction();
  }

  _removeMember(payload) {
    this._addSystemMessage(payload);
    this._toggleLeaveRoomAction();
  }

  _leaveRoom(payload) {
    this._addSystemMessage(payload);
  }

  _updateMembersCount(payload) {
    this.membersCountTarget.innerHTML = payload.count;
    this._removeSidePanel();
  }

  _addSystemMessage(payload) {
    const messageHTML = this._buildSystemMessageHTML(payload);
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHTML);
    this.scrollToBottom();
  }

  _addRoomAdmin(payload) {
    const member = this._getMemberContainer(payload.id);
    if (!member) return;

    this._addAdminRole(member);
    this._updateAdminMenu(member, payload.id, true);
  }

  _removeRoomAdmin(payload) {
    const member = this._getMemberContainer(payload.id);
    if (!member) return;

    this._removeAdminRole(member);
    this._updateAdminMenu(member, payload.id, false);
  }

  _getMessageContainer(id) {
    return document.getElementById(`room-message-${id}`);
  }

  _getMemberContainer(id) {
    return document.getElementById(`room-member-${id}`);
  }

  _isOwnMessage(profileId) {
    return this.profileId === profileId;
  }

  _resetCurrentUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('room_id');
    window.history.pushState({ path: url.href }, '', url.href);
  }

  _addEditedLabel(messageContainer, editedLabel) {
    const statusContainer = messageContainer.querySelector(this._sel('MESSAGE_STATUS'));
    if (!statusContainer) return;

    const existingLabel = statusContainer.querySelector(this._sel('MESSAGE_EDITED_STATUS'));
    if (existingLabel) return;

    const editedStatus = document.createElement('div');
    editedStatus.className = `${this._cls('MESSAGE_EDITED_STATUS')} ml-1 text-xs text-secondary`;
    editedStatus.textContent = editedLabel;
    statusContainer.appendChild(editedStatus);
  }

  _updateDeletedMessageContent(messageContainer, content) {
    const contentElement = messageContainer.querySelector(this._sel('MESSAGE_CONTENT'));
    if (!contentElement) return;

    contentElement.innerHTML = content;
    contentElement.classList.add('italic', 'text-secondary');

    const imagesContainer = messageContainer.querySelector(this._sel('MESSAGE_IMAGES'));
    if (imagesContainer) {
      imagesContainer.remove();
    }
    const statusContainer = messageContainer.querySelector(this._sel('MESSAGE_STATUS'));
    const editedStatus = statusContainer?.querySelector(this._sel('MESSAGE_EDITED_STATUS'));
    if (editedStatus) {
      editedStatus.remove();
    }
  }

  _removeMessageActions(messageContainer) {
    const actionsElement = messageContainer.querySelector(this._sel('MESSAGE_ACTIONS'));
    const menuElement = messageContainer.querySelector(this._sel('MESSAGE_MENU'));

    if (actionsElement) actionsElement.remove();
    if (menuElement) menuElement.remove();
  }

  _removeSourceMessage(messageContainer) {
    const sourceMessageElement = messageContainer.querySelector(this._sel('MESSAGE_SOURCE_MESSAGE'));

    if (sourceMessageElement) sourceMessageElement.remove();
  }

  _removeSourceMessageReferences(messageId) {
    const sourceContainers = document.querySelectorAll(`.room-message__source-message-${messageId}`);
    sourceContainers.forEach(container => container.remove());
  }

  _addAdminRole(member) {
    const memberFirstPart = member.querySelector(this._sel('MEMBER_TITLE_FIRST_PART'));
    if (!memberFirstPart) return;

    const existingRole = memberFirstPart.querySelector(this._sel('MEMBER_ROLE'));
    if (existingRole) return;

    const roleElement = document.createElement('div');
    roleElement.className = `ml-1 text-xs text-secondary ${this._cls('MEMBER_ROLE')}`;
    roleElement.textContent = '(admin)';
    memberFirstPart.appendChild(roleElement);
  }

  _removeAdminRole(member) {
    const memberRole = member.querySelector(this._sel('MEMBER_ROLE'));
    if (memberRole) {
      memberRole.remove();
    }
  }

  _updateAdminMenu(member, memberId, isAddingAdmin) {
    const menu = member.querySelector(this._sel('MEMBER_MENU'));
    if (!menu) return;

    if (isAddingAdmin) {
      this._replaceAddAdminWithRemove(menu, memberId);
      this._removeLeaveRoomAction(menu);
    } else {
      this._replaceRemoveAdminWithAdd(menu, memberId);
      this._addLeaveRoomAction(menu, memberId);
    }
  }

  _replaceAddAdminWithRemove(menu, memberId) {
    const addAdminSection = menu.querySelector(this._sel('MEMBER_MENU_ADD_ADMIN'));
    if (!addAdminSection) return;

    const removeAdminSection = document.createElement('div');
    removeAdminSection.className = this._cls('MEMBER_MENU_REMOVE_ADMIN');
    removeAdminSection.innerHTML = this._buildRemoveAdminForm(memberId);
    addAdminSection.replaceWith(removeAdminSection);
  }

  _replaceRemoveAdminWithAdd(menu, memberId) {
    const removeAdminSection = menu.querySelector(this._sel('MEMBER_MENU_REMOVE_ADMIN'));
    if (!removeAdminSection) return;

    const addAdminSection = document.createElement('div');
    addAdminSection.className = this._cls('MEMBER_MENU_ADD_ADMIN');
    addAdminSection.innerHTML = this._buildAddAdminForm(memberId);
    removeAdminSection.replaceWith(addAdminSection);
  }

  _removeLeaveRoomAction(menu) {
    const removeAction = menu.querySelector(this.constructor.DATA_ATTRS.ACTION_TYPE_LEAVE)?.closest('form');
    if (removeAction) {
      removeAction.remove();
    }
  }

  _addLeaveRoomAction(menu, memberId) {
    const existingAction = menu.querySelector(this.constructor.DATA_ATTRS.ACTION_TYPE_LEAVE);
    if (existingAction) return;

    const removeFromRoomAction = document.createElement('form');
    removeFromRoomAction.className = 'edit-action';
    removeFromRoomAction.method = 'get';
    removeFromRoomAction.action = '/action_confirmation';
    removeFromRoomAction.innerHTML = this._buildRemoveFromRoomForm(memberId);
    menu.appendChild(removeFromRoomAction);
  }

  _buildMessageHTML(payload, isOwnMessage) {
    const messageId = `room-message-${payload.id}`;

    return `
    <div
      class="${this._cls('MESSAGE')} flex px-6 py-2 transition-colors relative ${isOwnMessage ? 'justify-end' : ''}"
      id="${messageId}"
      data-controller="room-message-menu"
      data-action="mouseover->room-message-menu#enableHover mouseout->room-message-menu#disableHover"
      data-message-id="${payload.id}"
      ${isOwnMessage ? 'data-room-message-menu-is-self-message-value=""' : ''}
      data-room-message-menu-target="message"
    >
      ${!isOwnMessage ? this._buildAvatar(payload) : ''}
      ${isOwnMessage ? this._buildActionsButton(isOwnMessage) : ''}

      <div class="${this._cls('MESSAGE_CONTAINER')}">
        ${this._buildMessageBubble(payload, isOwnMessage)}
      </div>

      ${!isOwnMessage ? this._buildActionsButton(isOwnMessage) : ''}
      ${this._buildMenu(payload, isOwnMessage)}
    </div>
  `;
  }

  _buildAvatar(payload) {
    return `
    <div class="w-8 h-8">
      <div class="w-full h-full rounded overflow-hidden">
        <img
          class="object-cover object-center w-full h-full"
          src="${this._escapeHtml(payload.profileAvatarUrl)}"
        >
      </div>
    </div>
  `;
  }

  _buildSourceMessage(payload, bgColorClass) {
    if (!payload.sourceMessageId) return '';

    const image = payload.sourceMessageImage;
    const imageHtml = image
      ? `<div class="mb-1 ${this._cls('MESSAGE_SOURCE_MESSAGE_IMAGE')}" data-controller="lightbox">
        <a data-action="click->lightbox#showLightbox" href="${this._escapeHtml(image.url)}">
          <img class="w-24 h-24 object-cover rounded" src="${this._escapeHtml(image.url)}">
        </a>
      </div>`
      : '';

    const contentHtml = (payload.sourceMessageContent !== null && payload.sourceMessageContent !== undefined)
      ? `<div class="${this._cls('MESSAGE_SOURCE_MESSAGE_CONTENT')} text-sm">${this._escapeHtml(payload.sourceMessageContent)}</div>`
      : '';

    return `
    <div class="rounded px-2 py-1 ${bgColorClass} ${this._cls('MESSAGE_SOURCE_MESSAGE')} room-message__source-message-${payload.sourceMessageId}">
      <div class="text-xs font-bold">${this._escapeHtml(payload.sourceMessageProfileTitle)}</div>
      ${contentHtml}
      ${imageHtml}
    </div>
  `;
  }

  _buildMessageBubble(payload, isOwnMessage) {
    const contentId = `room-message-content-${payload.id}`;
    const bubbleClasses = isOwnMessage
      ? 'bg-secondary-light rounded-l-lg rounded-tr-lg'
      : 'bg-secondary-regular rounded-r-lg rounded-bl-lg';
    const sourceMessageBgColor = isOwnMessage ? 'bg-secondary-regular' : 'bg-secondary-light';
    const images = this._parseImageUrls(payload.imageUrls);

    return `
    <div class="${this._cls('MESSAGE_BUBBLE')} ml-2 px-2 py-1 ${bubbleClasses}">
      <div class="flex items-center ${this._cls('MESSAGE_STATUS')}">
        ${!isOwnMessage ? `<div class="mr-2 text-sm font-bold">${this._escapeHtml(payload.profileTitle)}</div>` : ''}
        <div class="text-xs">${this._escapeHtml(payload.messageTime)}</div>
      </div>

      ${this._buildSourceMessage(payload, sourceMessageBgColor)}

      ${this._buildMessageContent(payload, contentId)}

      ${images.length > 0 ? this._buildImageGallery(images) : ''}
    </div>
  `;
  }

  _buildMessageContent(payload, contentId) {
    if (payload.gifUrl) {
      return `
    <div class="${this._cls('MESSAGE_CONTENT')} mb-1">
      <img class="room-message__gif rounded" src="${this._escapeHtml(payload.gifUrl)}" alt="GIF">
    </div>
    `;
    }

    return `
    <span class="text-black ${this._cls('MESSAGE_CONTENT')}" id="${contentId}">
      ${payload.content ?? ''}
    </span>
  `;
  }

  _parseImageUrls(imageUrlsJson) {
    if (!imageUrlsJson) return [];

    try {
      return JSON.parse(imageUrlsJson);
    } catch (e) {
      console.error('Failed to parse imageUrls:', e);
      return [];
    }
  }

  _buildImageGallery(images) {
    if (!images.length) return '';

    const imageElements = images.map(img => `
      <a data-action="click->lightbox#showLightbox" href="${this._escapeHtml(img.url)}">
        <img class="w-32 h-32 object-cover rounded" src="${this._escapeHtml(img.url)}">
      </a>
    `).join('');

    return `
    <div class="mb-1 mt-1 flex flex-wrap gap-1 ${this._cls('MESSAGE_IMAGES')}" data-controller="lightbox">
      ${imageElements}
    </div>
  `;
  }

  _buildActionsButton(isOwnMessage) {
    return `
    <div
      class="${this._cls('MESSAGE_ACTIONS')} flex hidden"
      data-room-message-menu-target="actions"
    >
      <div
        ${!isOwnMessage ? 'class="ml-2"' : ''}
        data-action="
          click->room-message-menu#toggle
          keydown@window->room-message-menu#closeWithEscape
          click@window->room-message-menu#closeWithClickOutside
        "
      >
        <div
          class="w-6 h-6 flex justify-center items-center rounded cursor-pointer hover:bg-secondary-regular transition-colors relative inline-block"
          style="z-index: 9499;"
        >
          <i class="fas fa-ellipsis-h text-sm"></i>
        </div>
      </div>
    </div>
  `;
  }

  _buildMenu(payload, isOwnMessage) {
    return `
    <div
      class="trackkarma-menu ${this._cls('MESSAGE_MENU')} w-80 absolute hidden"
      data-room-message-menu-target="menu"
      style="z-index: 9999; ${isOwnMessage ? 'right: 40px; top: -160px;' : ''}"
    >
      <form class="button_to" method="get" action="/room_messages/${payload.id}/reactions/new">
        <button
          class="trackkarma-menu-item"
          data-action="click->room-message-menu#close"
          data-turbo-frame="modal"
          data-turbo="true"
          type="submit"
        >
          <div class="flex">
            <div class="w-6"><i class="fas fa-face-grin"></i></div>
            <span class="ml-2">${this.i18n.react}</span>
          </div>
        </button>
        <input type="hidden" name="action_type" value="create" autocomplete="off">
      </form>

      <form class="button_to" method="post" action="/room_messages/${payload.id}/reply">
        <button
          class="trackkarma-menu-item"
          data-action="click->room-message-menu#close click->room#focusInput"
          data-turbo="true"
          type="submit"
        >
          <div class="flex">
            <div class="w-6"><i class="fas fa-reply"></i></div>
            <span class="ml-2">${this.i18n.reply}</span>
          </div>
        </button>
        <input type="hidden" name="action_type" value="create" autocomplete="off">
      </form>

      <turbo-frame id="room-message-pin-${payload.id}">
        ${this._buildPinMenuItem(payload.id, !!payload.pinned)}
      </turbo-frame>

      <form class="button_to" method="get" action="/room_messages/${payload.id}/information">
        <button
          class="trackkarma-menu-item"
          data-action="click->room-message-menu#close click->room#focusInput"
          data-turbo-frame="modal"
          data-turbo="true"
          type="submit"
        >
          <div class="flex">
            <div class="w-6"><i class="fas fa-circle-info"></i></div>
            <span class="ml-2">${this.i18n.info}</span>
          </div>
        </button>
        <input type="hidden" name="action_type" value="show_information" autocomplete="off">
      </form>

      ${isOwnMessage ? this._buildOwnMessageMenuItems(payload) : ''}
    </div>
  `;
  }

  _buildPinMenuItem(messageId, pinned) {
    const label = pinned ? this.i18n.unpin : this.i18n.pin;
    const methodOverride = pinned
      ? '<input type="hidden" name="_method" value="delete" autocomplete="off">'
      : '';

    return `
    <form class="button_to" method="post" action="/room_messages/${messageId}/pin">
      ${methodOverride}
      <button
        class="trackkarma-menu-item"
        data-action="click->room-message-menu#close"
        data-turbo="true"
        type="submit"
      >
        <div class="flex">
          <div class="w-6"><i class="fas fa-thumbtack"></i></div>
          <span class="ml-2">${label}</span>
        </div>
      </button>
      <input type="hidden" name="action_type" value="create" autocomplete="off">
    </form>
  `;
  }

  _buildOwnMessageMenuItems(payload) {
    const messageId = payload.id;
    const isGif = !!payload.gifUrl;

    return `
    ${!isGif ? `
    <form method="get" action="/room_messages/${messageId}/edit">
      <button
        class="trackkarma-menu-item"
        data-action="click->room-message-menu#close"
        data-turbo-frame="modal"
        data-turbo="true"
        type="submit"
      >
        <div class="flex">
          <div class="w-6"><i class="fas fa-pencil"></i></div>
          <span class="ml-2">${this.i18n.edit}</span>
        </div>
      </button>
    </form>
    ` : ''}

    <form method="get" action="/action_confirmation">
      <button
        class="trackkarma-menu-item--danger"
        data-action="click->room-message-menu#close"
        data-turbo-frame="modal"
        data-turbo="true"
        type="submit"
      >
        <div class="flex">
          <div class="w-6"><i class="fas fa-trash"></i></div>
          <span class="ml-2">${this.i18n.delete}</span>
        </div>
      </button>

      <input type="hidden" name="action_type" value="delete">
      <input type="hidden" name="content" value="${this.i18n.confirmDelete}">
      <input type="hidden" name="url" value="/room_messages/${messageId}">
    </form>
  `;
  }

  _buildRoomLogoHtml(payload) {
    return `
    <div class="w-full h-full rounded overflow-hidden">
      <img class="object-cover object-center w-full h-full" src="${payload.roomLogoUrl}">
    </div>
    `;
  }

  _buildSystemMessageHTML(payload) {
    const messageId = `room-message-${payload.id}`;

    return `
    <div class="${this._cls('MESSAGE')} mt-4" id="${messageId}">
      <div class="text-center">
        <div class="text-xs">${this._escapeHtml(payload.messageTime)}</div>
        <div class="text-secondary">${payload.content}</div>
      </div>
    </div>
    `;
  }

  _buildRemoveAdminForm(memberId) {
    return `
    <form class="edit-action" method="get" action="/action_confirmation">
      <button class="trackkarma-menu-item--danger" data-cy="remove_admin" data-action="click->long-press#hide click->toggle#toggle" data-turbo-frame="modal" data-turbo="true" type="submit">
        <div class="flex">
          <div class="w-6"><i class="fas fa-user-shield"></i></div>
          <span class="ml-2">Remove as admin</span>
        </div>
      </button>
      <input type="hidden" name="action_type" value="remove_admin" autocomplete="off">
      <input type="hidden" name="button_title" value="Remove" autocomplete="off">
      <input type="hidden" name="content" value="Are you sure you want to remove this member from the admin list ?" autocomplete="off">
      <input type="hidden" name="title" value="Remove as admin" autocomplete="off">
      <input type="hidden" name="url" value="/room_profiles/${memberId}/room_admin" autocomplete="off">
    </form>
  `;
  }

  _buildAddAdminForm(memberId) {
    return `
    <form class="button_to" method="post" action="/room_profiles/${memberId}/room_admin">
      <button class="trackkarma-menu-item" data-cy="add_admin" data-action="click->long-press#hide click->toggle#toggle" data-turbo-frame="modal" data-turbo="true" type="submit">
        <div class="flex">
          <div class="w-6"><i class="fas fa-user-shield"></i></div>
          <span class="ml-2">Make admin</span>
        </div>
      </button>
      <input type="hidden" name="authenticity_token" value="" autocomplete="off">
      <input type="hidden" name="action_type" value="add_admin" autocomplete="off">
    </form>
  `;
  }

  _buildRemoveFromRoomForm(memberId) {
    return `
    <button class="trackkarma-menu-item--danger" data-cy="leave" data-action="click->long-press#hide click->toggle#toggle" data-turbo-frame="modal" data-turbo="true" type="submit">
      <div class="flex">
        <div class="w-6"><i class="fas fa-sign-out-alt"></i></div>
        <span class="ml-2">Remove from room</span>
      </div>
    </button>
    <input type="hidden" name="action_type" value="leave" autocomplete="off">
    <input type="hidden" name="button_title" value="Remove" autocomplete="off">
    <input type="hidden" name="content" value="Are you sure you want to remove this member from the room ?" autocomplete="off">
    <input type="hidden" name="title" value="Remove from room" autocomplete="off">
    <input type="hidden" name="url" value="/room_profiles/${memberId}" autocomplete="off">
  `;
  }

  _updateUrl(roomId = null) {
    const url = new URL(window.location.href);

    if (roomId) {
      url.searchParams.set('room_id', roomId);
    } else {
      url.searchParams.delete('room_id');
    }

    window.history.pushState({ path: url.href }, '', url.href);
  }

  clearMessage(event) {
    this.newMessageTarget.value = '';
  }

  _removeSidePanel() {
    const sidePanelContent = document.getElementById(this.constructor.IDS.SIDE_PANEL_CONTENT);
    if (sidePanelContent?.parentNode) {
      sidePanelContent.parentNode.removeChild(sidePanelContent);
    }
  }

  _toggleLeaveRoomAction() {
    const leaveRoomButton = document.querySelector(this.constructor.DATA_ATTRS.LEAVE_BUTTON);

    if (!leaveRoomButton) return;

    const leaveRoomForm = leaveRoomButton.closest('form');
    if (!leaveRoomForm) return;

    const membersCount = parseInt(this.membersCountTarget.innerHTML);
    if (membersCount > 2) {
      leaveRoomForm.classList.remove('hidden');
    } else {
      leaveRoomForm.classList.add('hidden');
    }
  }

  _escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  // Helper methods to convert CLASSES class names to selectors and vice versa
  _cls(key) {
    return this.constructor.CLASSES[key];
  }

  _sel(key) {
    return `.${this.constructor.CLASSES[key]}`;
  }
}